import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  NotificationService,
  SelectOption,
  SkeletonLoaderComponent,
  StenaAffirmOrDismissDialogComponent,
  StenaAffirmOrDismissDialogData,
  StenaButtonComponent,
  StenaSelectComponent,
} from '@app-monorepo/shared/ui/stena-components';
import { UsersApiService } from '../../../services/users-api.service';
import {
  CostCenter,
  KpiArea,
  User,
  UserAccess,
} from '../../../models/user.model';

@Component({
  standalone: true,
  selector: 'app-admin-users-page',
  templateUrl: './admin-users-page.component.html',
  styleUrl: './admin-users-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatCheckboxModule,
    SkeletonLoaderComponent,
    StenaButtonComponent,
    StenaSelectComponent,
  ],
})
export class AdminUsersPageComponent implements OnInit {
  private readonly api = inject(UsersApiService);
  private readonly notify = inject(NotificationService);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);

  readonly users = signal<User[]>([]);
  readonly costCenters = signal<CostCenter[]>([]);
  readonly kpiAreas = signal<KpiArea[]>([]);
  readonly selectedUserId = signal<number | null>(null);
  readonly userAccess = signal<UserAccess[]>([]);

  readonly loading = signal(false);
  readonly busy = signal(false);

  readonly form = this.fb.group({
    userId: this.fb.control<number | null>(null),
    newCostCenterId: this.fb.control<number | null>(null),
    newAreaId: this.fb.control<number | null>(null),
  });

  readonly selectedUser = computed(() => {
    const id = this.selectedUserId();
    return this.users().find((u) => u.accessId === id) ?? null;
  });

  readonly userOptions = computed<SelectOption<number>[]>(() =>
    this.users().map((u) => ({
      displayName: u.accessAdministrator
        ? `${u.accessUserName} (admin)`
        : u.accessUserName,
      value: u.accessId,
    }))
  );

  readonly costCenterOptions = computed<SelectOption<number>[]>(() =>
    this.costCenters().map((c) => ({
      displayName: c.costCenterName,
      value: c.costCenterId,
    }))
  );

  readonly kpiAreaOptions = computed<SelectOption<number>[]>(() =>
    this.kpiAreas().map((a) => ({
      displayName: a.areaName,
      value: a.areaId,
    }))
  );

  constructor() {
    this.form.controls.userId.valueChanges.subscribe((id) =>
      this.onSelectUser(id)
    );
  }

  ngOnInit(): void {
    this.loading.set(true);
    Promise.all([
      this.loadUsers(),
      this.loadCostCenters(),
      this.loadKpiAreas(),
    ]).finally(() => this.loading.set(false));
  }

  onSelectUser(accessId: number | null): void {
    this.selectedUserId.set(accessId);
    this.userAccess.set([]);
    if (accessId !== null) {
      this.api.access(accessId).subscribe({
        next: (rows) => this.userAccess.set(rows),
        error: () => this.err('Failed to load user access'),
      });
    }
  }

  onToggleDailyReporting(checked: boolean): void {
    const user = this.selectedUser();
    if (!user) return;
    this.busy.set(true);
    this.api.updateDailyReporting(user.accessId, checked).subscribe({
      next: (updated) => {
        this.users.update((list) =>
          list.map((u) => (u.accessId === updated.accessId ? updated : u))
        );
        this.busy.set(false);
      },
      error: () => {
        this.err('Failed to update daily reporting');
        this.busy.set(false);
      },
    });
  }

  onGrantAccess(): void {
    const user = this.selectedUser();
    const { newCostCenterId, newAreaId } = this.form.getRawValue();
    if (!user || newCostCenterId === null || newAreaId === null) {
      this.notify.warning('Select user, cost center and KPI area');
      return;
    }
    this.busy.set(true);
    this.api
      .grantAccess({
        accessId: user.accessId,
        costCenterId: newCostCenterId,
        areaId: newAreaId,
      })
      .subscribe({
        next: (rows) => {
          this.userAccess.set(rows);
          this.form.controls.newCostCenterId.setValue(null);
          this.form.controls.newAreaId.setValue(null);
          this.busy.set(false);
        },
        error: () => {
          this.err('Failed to grant access');
          this.busy.set(false);
        },
      });
  }

  onRevoke(row: UserAccess): void {
    const user = this.selectedUser();
    if (!user) return;
    this.busy.set(true);
    this.api
      .revokeAccess(user.accessId, row.costCenterId, row.areaId)
      .subscribe({
        next: (rows) => {
          this.userAccess.set(rows);
          this.busy.set(false);
        },
        error: () => {
          this.err('Failed to revoke access');
          this.busy.set(false);
        },
      });
  }

  async onDeleteUser(): Promise<void> {
    const user = this.selectedUser();
    if (!user) return;
    const data: StenaAffirmOrDismissDialogData = {
      title: 'Delete user',
      description: `Delete user ${user.accessUserName}?`,
      affirmButtonText: 'Delete',
      dismissButtonText: 'Cancel',
    };
    const ref = this.dialog.open(StenaAffirmOrDismissDialogComponent, { data });
    const confirmed = await firstValueFrom(ref.afterClosed());
    if (!confirmed) return;
    this.busy.set(true);
    this.api.deleteUser(user.accessId).subscribe({
      next: () => {
        this.users.update((list) =>
          list.filter((u) => u.accessId !== user.accessId)
        );
        this.selectedUserId.set(null);
        this.form.controls.userId.setValue(null, { emitEvent: false });
        this.userAccess.set([]);
        this.busy.set(false);
      },
      error: () => {
        this.err('Failed to delete user');
        this.busy.set(false);
      },
    });
  }

  private loadUsers(): Promise<void> {
    return new Promise((resolve) => {
      this.api.list().subscribe({
        next: (users) => {
          this.users.set(users);
          resolve();
        },
        error: () => {
          this.err('Failed to load users');
          resolve();
        },
      });
    });
  }

  private loadCostCenters(): Promise<void> {
    return new Promise((resolve) => {
      this.api.costCenters().subscribe({
        next: (rows) => {
          this.costCenters.set(rows);
          resolve();
        },
        error: () => {
          this.err('Failed to load cost centers');
          resolve();
        },
      });
    });
  }

  private loadKpiAreas(): Promise<void> {
    return new Promise((resolve) => {
      this.api.kpiAreas().subscribe({
        next: (rows) => {
          this.kpiAreas.set(rows);
          resolve();
        },
        error: () => {
          this.err('Failed to load KPI areas');
          resolve();
        },
      });
    });
  }

  private err(message: string): void {
    this.notify.error(message);
  }
}
